<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { createI18n, LocalePicker, M6_PICKER_LOCALES } from '@mybonet/i18n'
import ThemePackageHost from './ThemePackageHost.vue'
import { applyAlbumImagesJsonLd, applyBreadcrumbListJsonLd, applyDocumentSeo, applyFaqPageJsonLd, applyHowToJsonLd, applyNewsArticlesJsonLd, applyProductOffersJsonLd, applyTeamPersonsJsonLd, applyVideoObjectsJsonLd, extractContactFromHtml, extractCssVarHex, extractCtasFromHtml, extractFeaturesFromHtml, extractSeoFromHtml, extractSocialFromHtml, extractSpecsFromHtml, extractTeamFromHtml } from './seo'

type ThemePackage = {
  cdnBase: string
  prefix: string
  body: string
  css: string[]
  js: string[]
  fonts?: string[]
  images?: Record<string, string>
}
type Theme = {
  themeKey: string
  name: string
  category?: string
  description: string
  tokens: Record<string, string>
  package?: ThemePackage | null
}
type Page = { slug: string; title: string; bodyHtml: string; isHome: boolean }
type Bootstrap = {
  siteId: string
  siteKey: string
  displayName: string
  primaryHost: string
  vueAppKey: string
  themeKey: string
  theme: Theme
  pages: Page[]
  locale: string
  availableLocales: string[]
  localeStrings: Record<string, string>
  siteAssetOverrides?: Record<string, string> | null
}
type Product = {
  id: string
  sku: string
  name: string
  description?: string
  price: number
  currency: string
  stockQty: number
  status: string
  coverImageUrl?: string | null
  coverObjectKey?: string | null
}
type PublicAlbum = {
  id: string
  title: string
  description?: string | null
  sortOrder: number
  assets: { id: string; objectKey: string; title?: string | null; sortOrder: number; url: string }[]
}

const _api = import.meta.env.VITE_API_BASE
const API_BASE =
  _api !== undefined ? _api : import.meta.env.PROD ? '' : 'http://127.0.0.1:5055'
const bootstrap = ref<Bootstrap | null>(null)
const products = ref<Product[]>([])
const albums = ref<PublicAlbum[]>([])
const videos = ref<{ id: string; slug: string; title: string; bodyHtml?: string | null }[]>([])
const news = ref<{ id: string; slug: string; title: string; bodyHtml?: string | null }[]>([])
const announces = ref<{ id: string; slug: string; title: string; bodyHtml?: string | null }[]>([])
const contactInfo = computed(() => {
  const html = bootstrap.value?.pages.find((p) => p.slug === 'contact')?.bodyHtml
  return html ? extractContactFromHtml(html) : {}
})
const socialInfo = computed(() => {
  const html = bootstrap.value?.pages.find((p) => p.slug === 'contact')?.bodyHtml
  return html ? extractSocialFromHtml(html) : []
})
const featuresInfo = computed(() => {
  const pages = bootstrap.value?.pages || []
  const fromFeatures = pages.find((p) => p.slug === 'features')?.bodyHtml
  const fromHome = pages.find((p) => p.isHome)?.bodyHtml
  const parsed = extractFeaturesFromHtml(fromFeatures || '')
  return parsed.length > 0 ? parsed : extractFeaturesFromHtml(fromHome || '')
})
const ctasInfo = computed(() => {
  const html = bootstrap.value?.pages.find((p) => p.isHome)?.bodyHtml
  return html ? extractCtasFromHtml(html) : []
})
const teamInfo = computed(() => {
  const html = bootstrap.value?.pages.find((p) => p.slug === 'team')?.bodyHtml
  return html ? extractTeamFromHtml(html) : []
})
const specsInfo = computed(() => {
  const html = bootstrap.value?.pages.find((p) => p.slug === 'specs')?.bodyHtml
  return html ? extractSpecsFromHtml(html) : []
})
const error = ref('')
const slug = ref('home')
const menuOpen = ref(false)
const orderMsg = ref('')
const lookupMsg = ref('')
const i18n = createI18n({ enabled: [...M6_PICKER_LOCALES] })
const logoUrl = `${import.meta.env.BASE_URL}assets/images/logo.png`

const form = ref({
  name: '',
  phone: '',
  email: '',
  address: '',
  note: '',
  productId: '',
  qty: 1,
})
const lookupNo = ref('')

const page = computed(() => {
  const pages = bootstrap.value?.pages ?? []
  return pages.find((p) => p.slug === slug.value) ?? pages.find((p) => p.isHome) ?? pages[0]
})

const hasThemePackage = computed(() => !!bootstrap.value?.theme?.package)

const isMarketing = computed(() => {
  const t = bootstrap.value?.theme
  if (!t) return false
  return t.category === 'marketing' || t.tokens?.['--mkt-layout'] === 'landing'
})

function siteKeyFromSubdomain(): string | null {
  const host = (location.hostname || '').toLowerCase()
  const reserved = new Set(['mybo.net', 'www.mybo.net', 'hq.mybo.net', 'data.mybo.net', 'localhost', '127.0.0.1'])
  if (!reserved.has(host) && host.endsWith('.mybo.net')) {
    return host.slice(0, -'.mybo.net'.length)
  }
  if (!reserved.has(host) && host.endsWith('.localhost') && host !== 'hq.localhost') {
    return host.slice(0, -'.localhost'.length)
  }
  return new URLSearchParams(location.search).get('site')
}

/** Resolve siteKey for platform subdomain, ?site=, or custom domain via Host → tenancy/context. */
async function resolveSiteKey(): Promise<string> {
  const fromHost = siteKeyFromSubdomain()
  if (fromHost) return fromHost
  try {
    const res = await fetch(`${API_BASE}/api/v2/tenancy/context`)
    if (res.ok) {
      const data = await res.json()
      if (data?.siteKey) return String(data.siteKey)
    }
  } catch {
    /* fall through */
  }
  return 'www'
}

function closeMenu() {
  menuOpen.value = false
  document.body.classList.remove('nav-drawer-open')
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value
  document.body.classList.toggle('nav-drawer-open', menuOpen.value)
}

function goPage(next: string) {
  slug.value = next
  closeMenu()
  const el = document.getElementById(`sec-${next}`)
  if (el) el.scrollIntoView({ behavior: 'smooth' })
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  Object.entries(theme.tokens || {}).forEach(([k, v]) => root.style.setProperty(k, v))
  // Prefer body font for chrome; display font is for headings (see style.css).
  const body = theme.tokens?.['--font-body'] || theme.tokens?.['--font-display']
  const cjk = theme.tokens?.['--font-cjk']
  if (body && cjk) root.style.setProperty('font-family', `${body}, ${cjk}`)
  else if (body) root.style.setProperty('font-family', body)
}

async function loadProducts(siteKey: string) {
  const res = await fetch(`${API_BASE}/api/v2/storefront/${encodeURIComponent(siteKey)}/products`)
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || res.statusText)
  products.value = data.items || []
  if (!form.value.productId && products.value[0]) form.value.productId = products.value[0].id
}

async function loadAlbums(siteKey: string) {
  try {
    const res = await fetch(`${API_BASE}/api/v2/storefront/${encodeURIComponent(siteKey)}/albums`)
    const data = await res.json()
    if (!res.ok) return
    albums.value = data.items || []
  } catch {
    albums.value = []
  }
}

async function loadVideos(siteKey: string) {
  try {
    const res = await fetch(`${API_BASE}/api/v2/videos?siteKey=${encodeURIComponent(siteKey)}`)
    const data = await res.json()
    if (!res.ok) return
    videos.value = Array.isArray(data) ? data : data.items || []
  } catch {
    videos.value = []
  }
}

async function loadNews(siteKey: string) {
  try {
    const res = await fetch(`${API_BASE}/api/v2/news?siteKey=${encodeURIComponent(siteKey)}`)
    const data = await res.json()
    if (!res.ok) return
    news.value = Array.isArray(data) ? data : data.items || []
  } catch {
    news.value = []
  }
}

async function loadAnnounces(siteKey: string) {
  try {
    const res = await fetch(`${API_BASE}/api/v2/announces?siteKey=${encodeURIComponent(siteKey)}`)
    const data = await res.json()
    if (!res.ok) return
    announces.value = Array.isArray(data) ? data : data.items || []
  } catch {
    announces.value = []
  }
}

async function load() {
  error.value = ''
  const siteKey = await resolveSiteKey()
  try {
    // siteKey from subdomain / ?site= / tenancy context (custom Host). Bootstrap also accepts Host-only.
    const res = await fetch(
      `${API_BASE}/api/v2/bootstrap?siteKey=${encodeURIComponent(siteKey)}&locale=${encodeURIComponent(i18n.locale.value)}`,
    )
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || res.statusText)
    applyTheme(data.theme)
    // Package scripts use top-level lexical declarations. Mounting the package
    // before each public-data request settles would execute theme.js repeatedly
    // and fail with "Identifier ... has already been declared".
    await Promise.allSettled([
      loadProducts(siteKey),
      loadAlbums(siteKey),
      loadVideos(siteKey),
      loadNews(siteKey),
      loadAnnounces(siteKey),
    ])
    bootstrap.value = data
    slug.value = data.pages.find((p: Page) => p.isHome)?.slug || data.pages[0]?.slug || 'home'
    await applySeoFromBootstrap(data)
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'bootstrap_failed'
  }
}

async function applySeoFromBootstrap(data: Bootstrap) {
  const home = data.pages.find((p) => p.isHome) || data.pages[0]
  const fromHtml = home?.bodyHtml ? extractSeoFromHtml(home.bodyHtml) : {}
  const seoPage = data.pages.find((p) => p.slug === 'seo')
  const fromSeoPage = seoPage?.bodyHtml ? extractSeoFromHtml(seoPage.bodyHtml) : {}
  const pkg = data.theme.package
  let imageUrl = ''
  if (pkg?.images) {
    const hero = pkg.images['hero.jpg'] || Object.values(pkg.images)[0]
    if (hero) {
      const override = data.siteAssetOverrides?.[hero]
      imageUrl = override || `${pkg.cdnBase.replace(/\/$/, '')}/${pkg.prefix.replace(/^\/|\/$/g, '')}/${hero.replace(/^\//, '')}`
    }
  }
  const contactPage = data.pages.find((p) => p.slug === 'contact')
  const contact = contactPage?.bodyHtml ? extractContactFromHtml(contactPage.bodyHtml) : {}
  const social = contactPage?.bodyHtml ? extractSocialFromHtml(contactPage.bodyHtml) : []
  const canonicalUrl = `${location.protocol}//${location.host}/`
  let themeColor = data.theme.tokens?.['--color-accent']
    || data.theme.tokens?.['--color-primary']
    || data.theme.tokens?.['--color-bg']
    || '#0a0a0a'
  // Prompt specialize writes site-scoped tokens.override.css — prefer that for PWA theme-color.
  const tokenOverrideUrl = data.siteAssetOverrides?.['tokens.override.css']
  if (tokenOverrideUrl) {
    try {
      const res = await fetch(tokenOverrideUrl, { cache: 'force-cache' })
      if (res.ok) {
        const css = await res.text()
        themeColor = extractCssVarHex(css, '--color-accent')
          || extractCssVarHex(css, '--color-primary')
          || extractCssVarHex(css, '--color-bg')
          || themeColor
      }
    } catch {
      /* keep bootstrap token fallback */
    }
  }
  applyDocumentSeo({
    title: fromHtml.title || fromSeoPage.title || home?.title || data.displayName,
    description: fromHtml.description || fromSeoPage.description || data.theme.description || data.displayName,
    siteName: data.displayName,
    locale: data.locale || i18n.locale.value,
    canonicalUrl,
    imageUrl,
    themeColor,
    address: contact.address,
    telephone: contact.telephone,
    email: contact.email,
    openingHours: contact.openingHours,
    sameAs: social.map((s) => s.href).filter((h) => h && h !== '#'),
  })
  applyBreadcrumbListJsonLd(data.pages, {
    canonicalUrl,
    siteName: data.displayName,
  })
}

watch(slug, () => {
  if (bootstrap.value) void applySeoFromBootstrap(bootstrap.value)
})

watch(
  products,
  (items) => {
    applyProductOffersJsonLd(items, {
      siteName: bootstrap.value?.displayName,
      canonicalUrl: `${location.protocol}//${location.host}/`,
    })
  },
  { deep: true },
)

watch(
  [featuresInfo, specsInfo, () => bootstrap.value?.displayName],
  ([features, specs, siteName]) => {
    const fromFeatures = (features || [])
      .filter((f) => f.description)
      .map((f) => ({ question: f.title, answer: f.description || '' }))
    const fromSpecs = (specs || []).map((s) => ({
      question: s.label,
      answer: s.value,
    }))
    applyFaqPageJsonLd([...fromFeatures, ...fromSpecs], { siteName: siteName || undefined })
    applyHowToJsonLd(features || [], {
      siteName: siteName || undefined,
      canonicalUrl: `${location.protocol}//${location.host}/`,
    })
  },
  { deep: true },
)

watch(
  [teamInfo, () => bootstrap.value?.displayName],
  ([members, siteName]) => {
    applyTeamPersonsJsonLd(members || [], {
      siteName: siteName || undefined,
      canonicalUrl: `${location.protocol}//${location.host}/`,
    })
  },
  { deep: true },
)

watch(
  [news, announces, () => bootstrap.value?.displayName],
  ([newsItems, announceItems, siteName]) => {
    applyNewsArticlesJsonLd([...(newsItems || []), ...(announceItems || [])], {
      siteName: siteName || undefined,
      canonicalUrl: `${location.protocol}//${location.host}/`,
    })
  },
  { deep: true },
)

watch(
  [videos, () => bootstrap.value?.displayName],
  ([videoItems, siteName]) => {
    applyVideoObjectsJsonLd(videoItems || [], {
      siteName: siteName || undefined,
      canonicalUrl: `${location.protocol}//${location.host}/`,
    })
  },
  { deep: true },
)

watch(
  [albums, () => bootstrap.value?.displayName],
  ([albumItems, siteName]) => {
    applyAlbumImagesJsonLd(albumItems || [], { siteName: siteName || undefined })
  },
  { deep: true },
)

async function submitOrder() {
  orderMsg.value = ''
  const siteKey = bootstrap.value?.siteKey
  if (!siteKey) return
  const p = products.value.find((x) => x.id === form.value.productId) || products.value[0]
  if (!p) {
    orderMsg.value = '尚無商品'
    return
  }
  const res = await fetch(`${API_BASE}/api/v2/storefront/${encodeURIComponent(siteKey)}/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerName: form.value.name,
      phone: form.value.phone,
      email: form.value.email,
      address: form.value.address,
      note: form.value.note,
      currency: p.currency || 'TWD',
      shippingAmount: 0,
      discountAmount: 0,
      paymentMethod: 'cod',
      lines: [{ productId: p.id, productName: p.name, unitPrice: p.price, quantity: form.value.qty }],
    }),
  })
  const data = await res.json()
  if (!res.ok) {
    orderMsg.value = data.error || '下單失敗'
    return
  }
  orderMsg.value = `訂單已建立：${data.orderNo}（可於下方查詢）`
  lookupNo.value = data.orderNo
}

async function lookupOrder() {
  lookupMsg.value = ''
  const siteKey = bootstrap.value?.siteKey
  if (!siteKey || !lookupNo.value.trim()) return
  const res = await fetch(
    `${API_BASE}/api/v2/storefront/${encodeURIComponent(siteKey)}/orders/${encodeURIComponent(lookupNo.value.trim())}`,
  )
  const data = await res.json()
  if (!res.ok) {
    lookupMsg.value = data.error || '查無訂單'
    return
  }
  lookupMsg.value = `${data.orderNo} · ${data.status} · ${data.currency} ${data.totalAmount} · ${data.lines?.[0]?.productName || ''}`
}

function onLocale(code: string) {
  i18n.setLocale(code)
  // Package bodies own their locale JSON and ThemePackageHost applies it in
  // place. Reloading all storefront data here would remount classic theme.js.
  if (hasThemePackage.value) return
  void load()
}

function money(p: Product) {
  return `${p.currency || 'TWD'} ${Number(p.price).toLocaleString()}`
}

onMounted(load)
onUnmounted(() => document.body.classList.remove('nav-drawer-open'))
</script>

<template>
  <!-- CDN design package: 100% design markup/CSS/JS from CloudFront -->
  <ThemePackageHost
    v-if="bootstrap && hasThemePackage"
    :bootstrap="bootstrap"
    :api-base="API_BASE"
    :products="products"
    :albums="albums"
    :videos="videos"
    :news="news"
    :announces="announces"
    :contact="contactInfo"
    :social="socialInfo"
    :features="featuresInfo"
    :ctas="ctasInfo"
    :team="teamInfo"
    :specs="specsInfo"
    :locale="i18n.locale.value"
    :locale-options="i18n.available.value"
    :locale-label="bootstrap.localeStrings['shell.lang'] || i18n.t('shell.lang')"
    @locale-change="onLocale"
  />

  <div class="shell" :class="{ marketing: isMarketing }" v-else-if="bootstrap">
    <nav class="shell-nav">
      <div class="shell-nav-inner">
        <a class="nav-logo" href="#" @click.prevent="goPage(bootstrap.pages.find(p => p.isHome)?.slug || 'home')">
          <img v-if="!isMarketing" :src="logoUrl" :alt="bootstrap.displayName" />
          <span>{{ bootstrap.displayName }}</span>
        </a>
        <ul class="nav-menu">
          <li v-for="p in bootstrap.pages" :key="p.slug">
            <a href="#" :class="{ active: slug === p.slug }" @click.prevent="goPage(p.slug)">{{ p.title }}</a>
          </li>
          <li v-if="isMarketing"><a href="#sec-products" @click.prevent="goPage('products')">商品</a></li>
          <li v-if="isMarketing"><a href="#sec-order" @click.prevent="goPage('order')">訂購</a></li>
        </ul>
        <div class="nav-actions">
          <LocalePicker
            class="nav-desktop-only"
            :locale="i18n.locale.value"
            :options="i18n.available.value"
            :label="bootstrap.localeStrings['shell.lang'] || i18n.t('shell.lang')"
            @change="onLocale"
          />
          <button class="nav-menu-toggle" type="button" :aria-expanded="menuOpen" @click="toggleMenu">☰</button>
        </div>
      </div>
    </nav>

    <button class="nav-drawer-backdrop" type="button" :class="{ open: menuOpen }" @click="closeMenu" />
    <aside class="nav-drawer" :class="{ open: menuOpen }">
      <ul class="nav-drawer-menu">
        <li v-for="p in bootstrap.pages" :key="`d-${p.slug}`">
          <a href="#" @click.prevent="goPage(p.slug)">{{ p.title }}</a>
        </li>
      </ul>
    </aside>

    <template v-if="isMarketing">
      <section class="mkt-hero" id="sec-home">
        <div class="container">
          <div class="mkt-badge">{{ bootstrap.theme.name }} · {{ bootstrap.themeKey }}</div>
          <h1>{{ bootstrap.displayName }}</h1>
          <p class="mkt-sub">{{ bootstrap.theme.description }}</p>
          <div class="mkt-hero-body body" v-html="page?.bodyHtml" />
          <div class="mkt-cta">
            <a class="btn-accent" href="#sec-products" @click.prevent="goPage('products')">查看方案</a>
            <a class="btn-ghost" href="#sec-order" @click.prevent="goPage('order')">立即訂購</a>
          </div>
        </div>
      </section>

      <section class="mkt-section" id="sec-products">
        <div class="container">
          <div class="section-label">Products · from API</div>
          <h2>商品與方案</h2>
          <div class="product-grid">
            <article v-for="p in products" :key="p.id" class="product-card" :class="{ featured: p === products[1] }">
              <img v-if="p.coverImageUrl" :src="p.coverImageUrl" :alt="p.name" loading="lazy" class="product-cover" />
              <div class="sku">{{ p.sku }}</div>
              <h3>{{ p.name }}</h3>
              <p>{{ p.description || '—' }}</p>
              <div class="price">{{ money(p) }}</div>
              <button type="button" class="btn-accent" @click="form.productId = p.id; goPage('order')">選購</button>
            </article>
            <p v-if="!products.length" class="muted">尚無商品（請於子站後台新增）</p>
          </div>
        </div>
      </section>

      <section class="mkt-section alt" id="sec-order">
        <div class="container narrow">
          <div class="section-label">Checkout · API</div>
          <h2>訂購表單</h2>
          <form class="order-form" @submit.prevent="submitOrder">
            <label>姓名 <input v-model="form.name" required /></label>
            <label>電話 <input v-model="form.phone" required /></label>
            <label>Email <input v-model="form.email" type="email" /></label>
            <label>商品
              <select v-model="form.productId" required>
                <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }} — {{ money(p) }}</option>
              </select>
            </label>
            <label>數量 <input v-model.number="form.qty" type="number" min="1" required /></label>
            <label class="full">地址 <input v-model="form.address" /></label>
            <label class="full">備註 <textarea v-model="form.note" rows="2" /></label>
            <button class="btn-accent" type="submit">送出訂單</button>
            <p v-if="orderMsg" class="form-msg">{{ orderMsg }}</p>
          </form>

          <div class="lookup">
            <h3>訂單查詢</h3>
            <div class="lookup-row">
              <input v-model="lookupNo" placeholder="訂單編號 OyyyyMMdd-0001" />
              <button type="button" class="btn-ghost" @click="lookupOrder">查詢</button>
            </div>
            <p v-if="lookupMsg" class="form-msg">{{ lookupMsg }}</p>
          </div>
        </div>
      </section>

      <section v-for="p in bootstrap.pages.filter(x => !x.isHome)" :key="p.slug" class="mkt-section" :id="`sec-${p.slug}`">
        <div class="container content">
          <h2>{{ p.title }}</h2>
          <div class="body" v-html="p.bodyHtml" />
        </div>
      </section>
    </template>

    <template v-else>
      <section class="hero" v-if="page?.isHome || slug === (bootstrap.pages.find(p => p.isHome)?.slug)">
        <div class="container">
          <div class="section-label">{{ bootstrap.primaryHost }}</div>
          <h1 class="hero-title">{{ page?.title }}</h1>
          <div class="hero-desc body" v-html="page?.bodyHtml" />
        </div>
      </section>
      <section class="section" v-else>
        <div class="container content">
          <h2 class="section-title">{{ page?.title }}</h2>
          <div class="body" v-html="page?.bodyHtml" />
        </div>
      </section>
    </template>

    <footer class="foot">
      <div class="container foot-inner">
        <span>{{ bootstrap.displayName }} · {{ bootstrap.primaryHost }} · theme {{ bootstrap.themeKey }}</span>
        <button class="reload" type="button" @click="load">Reload</button>
      </div>
    </footer>
  </div>
  <div v-else-if="error" class="error">{{ error }}</div>
  <div v-else class="loading">…</div>
</template>

<style>
@import './style.css';

.shell.marketing {
  background: var(--color-bg, #0a0a0f);
  color: var(--color-ink, #eee);
  font-family: var(--font-display, inherit);
  min-height: 100vh;
}
.shell.marketing .shell-nav {
  background: color-mix(in srgb, var(--color-bg, #111) 88%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--color-accent, #888) 35%, transparent);
}
.shell.marketing .nav-logo span { color: var(--color-accent); letter-spacing: 0.08em; font-weight: 700; }
.mkt-hero { padding: 7rem 1.5rem 4rem; }
.mkt-badge {
  display: inline-block; font-size: 0.75rem; letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--color-accent); border: 1px solid var(--color-accent); padding: 0.35rem 0.9rem; margin-bottom: 1.2rem;
}
.mkt-hero h1 { font-size: clamp(2.2rem, 5vw, 3.8rem); line-height: 1.15; margin: 0 0 1rem; }
.mkt-sub { color: var(--color-muted); max-width: 36rem; line-height: 1.7; }
.mkt-hero-body { margin: 1.5rem 0; color: var(--color-muted); max-width: 40rem; }
.mkt-cta { display: flex; gap: 0.8rem; flex-wrap: wrap; margin-top: 1.5rem; }
.btn-accent, .btn-ghost {
  display: inline-block; padding: 0.85rem 1.6rem; text-decoration: none; border: 1px solid var(--color-accent);
  letter-spacing: 0.08em; text-transform: uppercase; font-size: 0.8rem; cursor: pointer;
}
.btn-accent { background: var(--color-accent); color: var(--color-bg, #111); }
.btn-ghost { background: transparent; color: var(--color-accent); }
.mkt-section { padding: 4rem 1.5rem; }
.mkt-section.alt { background: color-mix(in srgb, var(--color-ink, #fff) 4%, var(--color-bg, #111)); }
.section-label { font-size: 0.7rem; letter-spacing: 0.2em; text-transform: uppercase; color: var(--color-accent); margin-bottom: 0.6rem; }
.product-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-top: 1.5rem; }
.product-card {
  border: 1px solid color-mix(in srgb, var(--color-accent) 25%, transparent);
  padding: 1.4rem; background: color-mix(in srgb, var(--color-bg) 80%, #000);
}
.product-card.featured { border-color: var(--color-accent); box-shadow: 0 0 0 1px var(--color-accent); }
.product-card .sku { font-size: 0.7rem; color: var(--color-muted); letter-spacing: 0.1em; }
.product-card .product-cover { width: 100%; aspect-ratio: 4 / 3; object-fit: cover; margin-bottom: 0.8rem; display: block; }
.product-card .price { font-size: 1.6rem; color: var(--color-accent); margin: 0.8rem 0; font-weight: 700; }
.container.narrow { max-width: 640px; }
.order-form { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; margin-top: 1rem; }
.order-form label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.75rem; letter-spacing: 0.08em; color: var(--color-muted); }
.order-form .full { grid-column: 1 / -1; }
.order-form input, .order-form select, .order-form textarea {
  background: transparent; border: 1px solid color-mix(in srgb, var(--color-accent) 30%, transparent);
  color: var(--color-ink); padding: 0.7rem 0.8rem; font: inherit;
}
.form-msg { grid-column: 1 / -1; color: var(--color-accent); }
.lookup { margin-top: 2.5rem; padding-top: 1.5rem; border-top: 1px solid color-mix(in srgb, var(--color-accent) 20%, transparent); }
.lookup-row { display: flex; gap: 0.6rem; margin-top: 0.8rem; }
.lookup-row input { flex: 1; background: transparent; border: 1px solid color-mix(in srgb, var(--color-accent) 30%, transparent); color: var(--color-ink); padding: 0.7rem; }
.muted { color: var(--color-muted); }
@media (max-width: 640px) {
  .order-form { grid-template-columns: 1fr; }
}
</style>
